import { AlertController } from '@ionic/angular/standalone';

import { SyncMessage } from '../../../../../common/enums/sync-message.enum';
import { NetworkService } from '../../../../../common/services/network/network.service';

/** localStorage key remembering the user opted into backing up over mobile data
 *  (so we stop asking). Device-local by design — a data-plan preference belongs
 *  to the device, not the synced account. */
export const CELLULAR_OK_KEY = 'sequal.sync.cellularOk.v1';

/**
 * On mobile data, confirm before a backup/restore spends the user's data plan.
 * Returns true to proceed. No-op (true) on Wi-Fi / unknown connectivity, or once
 * the user has opted in with "Don't ask again". The opt-in is device-local.
 *
 * A free function rather than a method because both the backup sheet and the
 * cloud items list inside it can start a transfer, and the opt-in has to be the
 * same one — asking twice, or asking again after "don't ask", is the failure
 * this shared version exists to prevent.
 */
export async function confirmMeteredTransfer(
  deps: { alertCtrl: AlertController; network: NetworkService },
  kind: 'backup' | 'restore',
): Promise<boolean> {
  if (deps.network.connectionType() !== 'cellular') return true;
  try {
    if (localStorage.getItem(CELLULAR_OK_KEY) === '1') return true;
  } catch { /* storage blocked — fall through and ask */ }

  const alert = await deps.alertCtrl.create({
    header: SyncMessage.CellularHeader,
    message:
      kind === 'restore' ? SyncMessage.CellularRestoreMessage : SyncMessage.CellularBackupMessage,
    inputs: [
      { type: 'checkbox', label: SyncMessage.CellularDontAsk, value: 'dontAsk', checked: false },
    ],
    buttons: [
      { text: SyncMessage.CellularWait, role: 'cancel' },
      { text: SyncMessage.CellularContinue, role: 'confirm' },
    ],
  });
  await alert.present();
  const { role, data } = await alert.onDidDismiss<{ values?: string[] }>();
  if (role !== 'confirm') return false;
  if (data?.values?.includes('dontAsk')) {
    try { localStorage.setItem(CELLULAR_OK_KEY, '1'); } catch { /* non-fatal */ }
  }
  return true;
}
